import { ImageUp, Loader2 } from "lucide-react";
import { useRef, useState, type ChangeEvent, type MouseEventHandler } from "react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

import { IMAGE_UPLOAD_ACCEPTED_MIME_TYPES, IMAGE_UPLOAD_MAX_FILE_SIZE } from "../../config/uploads";
import { useEditorI18n } from "../../lib/i18n";
import { isLikelyImageUrl } from "../../lib/image-upload-node";
import type { UploadImageHandler } from "../../lib/image-upload-service";
import type { EditorContentMode } from "../../types";

type ReplaceImagePopoverProps = {
  contentMode: EditorContentMode;
  onMouseDown: MouseEventHandler<HTMLButtonElement>;
  onReplace: (src: string) => void;
  uploadImage?: UploadImageHandler;
};

type ReplaceSource = "upload" | "url" | null;

export function ReplaceImagePopover({
  contentMode,
  onMouseDown,
  onReplace,
  uploadImage,
}: ReplaceImagePopoverProps) {
  const t = useEditorI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadControllerRef = useRef<AbortController | null>(null);
  const validationImageRef = useRef<HTMLImageElement | null>(null);
  const [open, setOpen] = useState(false);
  const [remoteUrl, setRemoteUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [replaceSource, setReplaceSource] = useState<ReplaceSource>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isReplacing = replaceSource !== null;

  const resetState = () => {
    setRemoteUrl("");
    setErrorMessage("");
    setReplaceSource(null);
    setUploadProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setErrorMessage("");
      return;
    }

    uploadControllerRef.current?.abort();
    uploadControllerRef.current = null;

    if (validationImageRef.current) {
      validationImageRef.current.src = "";
      validationImageRef.current = null;
    }

    resetState();
  };

  const completeReplacement = (src: string) => {
    onReplace(src);
    handleOpenChange(false);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setErrorMessage("");

    if (!IMAGE_UPLOAD_ACCEPTED_MIME_TYPES.includes(file.type as (typeof IMAGE_UPLOAD_ACCEPTED_MIME_TYPES)[number])) {
      setErrorMessage(t("image.supportedTypes"));
      event.target.value = "";
      return;
    }

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      setErrorMessage(t("image.replaceHelper"));
      event.target.value = "";
      return;
    }

    if (!uploadImage) {
      setErrorMessage(t("image.uploadNotConfigured"));
      event.target.value = "";
      return;
    }

    const controller = new AbortController();
    uploadControllerRef.current = controller;
    setReplaceSource("upload");
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, {
        signal: controller.signal,
        onProgress: setUploadProgress,
      });
      const src = typeof result === "string" ? result : result.url;

      if (!src) {
        throw new Error("Image upload returned an empty URL.");
      }

      uploadControllerRef.current = null;
      completeReplacement(src);
    } catch (error) {
      if (!controller.signal.aborted) {
        setErrorMessage(error instanceof Error && error.message ? error.message : t("image.replaceFailed"));
        setReplaceSource(null);
      }
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoteReplace = () => {
    const src = remoteUrl.trim();

    setErrorMessage("");

    if (!isLikelyImageUrl(src)) {
      setErrorMessage(t("image.validUrlRequired"));
      return;
    }

    setReplaceSource("url");
    const image = new Image();
    validationImageRef.current = image;
    image.onload = () => {
      validationImageRef.current = null;
      completeReplacement(src);
    };
    image.onerror = () => {
      validationImageRef.current = null;
      setErrorMessage(t("image.couldNotLoadUrl"));
      setReplaceSource(null);
    };
    image.src = src;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          aria-label={t("image.replace")}
          className="image-bubble-menu-button"
          data-tooltip={t("image.replace")}
          onMouseDown={onMouseDown}
          type="button"
        >
          <ImageUp className="size-4" strokeWidth={1.9} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80" sideOffset={8}>
        <div className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-sm font-medium">{t("image.replaceTitle")}</h3>
            <p className="text-xs text-muted-foreground">
              {contentMode === "markdown" ? t("image.replaceMarkdownHint") : t("image.replaceHelper")}
            </p>
          </div>

          {contentMode === "tiptap-json" && (
            <>
              <input
                ref={fileInputRef}
                accept={IMAGE_UPLOAD_ACCEPTED_MIME_TYPES.join(",")}
                className="hidden"
                onChange={handleFileChange}
                type="file"
              />
              <Button
                className="w-full"
                disabled={isReplacing}
                onClick={() => fileInputRef.current?.click()}
                type="button"
                variant="outline"
              >
                {replaceSource === "upload" ? <Loader2 className="animate-spin" /> : <ImageUp />}
                {replaceSource === "upload"
                  ? t("image.uploadingProgress", { progress: uploadProgress })
                  : t("image.replaceUpload")}
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                <span>{t("image.orUseUrl")}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <div className="flex gap-2">
            <Input
              aria-invalid={Boolean(errorMessage)}
              disabled={isReplacing}
              onChange={(event) => setRemoteUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleRemoteReplace();
                }
              }}
              placeholder={t("image.urlPlaceholder")}
              value={remoteUrl}
            />
            <Button disabled={isReplacing} onClick={handleRemoteReplace} type="button">
              {replaceSource === "url" ? <Loader2 className="animate-spin" /> : t("image.replaceSubmit")}
            </Button>
          </div>

          {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
